import * as React from 'react';

import * as digitalData from '@ao-internal/digital-data';

import { ChatSession } from '../App';
import { TaggingEventNames } from '../globals';
import { getConversationSummary } from '../helpers/state';
import {
	ChatHistoryType,
	ChatMode,
	LiveChatStatus,
	SenderTypes,
	State,
} from '../store';
import usePrevious from './usePrevious';

function getFriendlyLiveChatStatus(status?: LiveChatStatus): string {
	switch (status) {
		case LiveChatStatus.Connecting:
			return 'connecting';
		case LiveChatStatus.Connected:
			return 'connected';
		case LiveChatStatus.Transferring:
			return 'transferring';
		default:
			return 'disconnected';
	}
}

function getFriendlyEndedBy(sender?: SenderTypes) {
	switch (sender) {
		case SenderTypes.User:
		case SenderTypes.Chatbot:
			return 'user';
		case SenderTypes.LiveChat:
			return 'agent';
		default:
			return 'system';
	}
}

export function sendConnectingDurationBeacon(duration: number): void {
	digitalData.sendBeacon(TaggingEventNames.connectingDuration, {
		duration: Math.floor(duration),
	});
}

export function sendTransferringDurationBeacon(duration: number): void {
	digitalData.sendBeacon(TaggingEventNames.transferringDuration, {
		duration: Math.floor(duration),
	});
}

export type UpdateAnalyticsProps = Pick<
	State,
	| 'chatMode'
	| 'chatHistory'
	| 'liveChatStatus'
	| 'liveChatExitedBy'
	| 'showLiveChatAutoDisconnectNotice'
	| 'liveChatActivationData'
	| 'liveChatAgent'
>;

export function useAnalytics({
	chatMode,
	chatHistory,
	liveChatStatus,
	liveChatExitedBy,
	showLiveChatAutoDisconnectNotice,
	liveChatActivationData,
	liveChatAgent,
}: UpdateAnalyticsProps): void {
	const prevLiveChatStatus = usePrevious(liveChatStatus);

	React.useEffect(() => {
		let newDigitalData: any = {
			page: {
				pageInfo: {
					chatbot: {
						chatMode: chatMode === ChatMode.ChatBot ? 'chatbot' : 'livechat',
					},
				},
			},
		};
		if (chatMode === ChatMode.ChatBot) {
			// Reset live chat props when chatMode === chatbot
			newDigitalData = {
				...newDigitalData,
				page: {
					...newDigitalData.page,
					pageInfo: {
						...newDigitalData.page.pageInfo,
						chatbot: {
							...newDigitalData.page.pageInfo.chatbot,
							liveChatStatus: undefined,
							liveChatInstanceId: undefined,
							liveChatWorkgroup: undefined,
							liveChatAgentName: undefined,
						},
					},
				},
			};
		}

		digitalData.manipulateDigitalData(newDigitalData);
	}, [chatMode]);

	React.useEffect(() => {
		if (
			// liveChatStatus !== undefined
			liveChatStatus !== prevLiveChatStatus &&
			liveChatStatus !== undefined &&
			!(
				liveChatStatus === LiveChatStatus.Disconnected &&
				prevLiveChatStatus === undefined
			)
		) {
			digitalData.manipulateDigitalData({
				page: {
					pageInfo: {
						chatbot: {
							liveChatStatus: getFriendlyLiveChatStatus(liveChatStatus),
							liveChatInstanceId: liveChatActivationData
								? liveChatActivationData.chatInstanceId
								: undefined,
							liveChatWorkgroup: liveChatActivationData
								? liveChatActivationData.targetWorkgroup
								: undefined,
							liveChatAgentName: liveChatAgent ? liveChatAgent.name : undefined,
						},
					},
				},
			});
		}
	}, [
		liveChatActivationData,
		liveChatAgent,
		liveChatStatus,
		prevLiveChatStatus,
	]);

	React.useEffect(() => {
		if (
			liveChatStatus !== prevLiveChatStatus &&
			prevLiveChatStatus !== undefined &&
			liveChatStatus === LiveChatStatus.Disconnected
		) {
			const {
				liveChatConnectedTime,
				liveChatUserTextMessageCount,
				liveChatAgentTextMessageCount,
			} = getConversationSummary(chatHistory);

			const wrapUpSummary = {
				conversationDuration: liveChatConnectedTime
					? Math.floor((Date.now() - liveChatConnectedTime) / 1000)
					: 0,
				userMessageCount: liveChatUserTextMessageCount,
				agentMessageCount: liveChatAgentTextMessageCount,
				endedBy: getFriendlyEndedBy(liveChatExitedBy),
				liveChatInstanceId: liveChatActivationData
					? liveChatActivationData.chatInstanceId
					: undefined,
			};

			digitalData.sendBeacon(
				TaggingEventNames.liveChatSessionDetails,
				wrapUpSummary
			);
		}
	}, [
		chatHistory,
		liveChatActivationData,
		liveChatExitedBy,
		liveChatStatus,
		prevLiveChatStatus,
	]);

	React.useEffect(() => {
		digitalData.manipulateDigitalData({
			page: {
				pageInfo: {
					chatbot: {
						liveChatAutoDisconnectNoticeDisplayed:
							!!showLiveChatAutoDisconnectNotice,
					},
				},
			},
		});
	}, [showLiveChatAutoDisconnectNotice]);
}

export default useAnalytics;
